// Iter 62ay — Carte réutilisable "Elevate Influence" affichée sur le
// Dashboard et la page Planning. Regroupe les tâches Elevate séparément
// des tâches ASA pour un tri visuel clair (bordure bleue + badge).
// Iter 62bm — Rétractable pour éviter la pollution visuelle. Persisté en
// localStorage. Cash reminders restent visibles même collapsed pour ne pas
// rater les alertes prioritaires.
import { useState } from 'react';
import { toast } from 'sonner';
import { useElevateTasks, useElevateCashReminders, completeElevateTask, deleteElevateTask } from '../hooks/useElevate';
import { CheckSquare, ChevronRight, Zap, X } from './icons';

const LS_KEY = 'asa.elevate-card.expanded';

export function ElevateInfluenceCard({ compact = false, onOpenAll }) {
  const { tasks: fetched, loading } = useElevateTasks();
  const cashReminders = useElevateCashReminders();
  const [localTasks, setLocalTasks] = useState(null);
  const tasks = localTasks ?? fetched;
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === '1'; } catch (_) { return false; }
  });
  const toggleExpanded = () => {
    setExpanded((v) => {
      const next = !v;
      try { localStorage.setItem(LS_KEY, next ? '1' : '0'); } catch (_) { /* localStorage full */ }
      if (navigator.vibrate) navigator.vibrate(8);
      return next;
    });
  };

  // Segment
  const openTasks = tasks.filter((t) => t.status !== 'done').slice(0, compact ? 3 : 6);
  const overdueCash = cashReminders.filter((r) => r.overdue && !r.completed);

  const toggle = async (t) => {
    const newStatus = t.status === 'done' ? 'todo' : 'done';
    const updated = (localTasks ?? tasks).map((x) => x.id === t.id ? { ...x, status: newStatus } : x);
    setLocalTasks(updated);
    try {
      const r = await completeElevateTask(t.id, newStatus);
      toast.success(r.remote_synced ? 'Sync Elevate ✓' : 'Coché localement');
      if (navigator.vibrate) navigator.vibrate(15);
    } catch (_) {
      toast.error('Erreur');
      setLocalTasks(null);
    }
  };

  if (loading && !localTasks) {
    return (
      <div className="asa-card" style={{ padding: 16 }}>
        <div className="shimmer" style={{ height: 60, borderRadius: 10 }} />
      </div>
    );
  }

  return (
    <div
      data-testid="elevate-card"
      className="asa-card"
      style={{
        padding: 14,
        border: '1.5px solid rgba(30,95,255,0.35)',
        background:
          'linear-gradient(140deg, rgba(30,95,255,0.10) 0%, rgba(30,95,255,0.02) 55%), var(--sf)',
      }}
    >
      {/* Header — clickable to toggle expand/collapse */}
      <button
        data-testid="elevate-card-toggle"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', padding: 0, margin: 0,
          marginBottom: expanded || overdueCash.length > 0 ? 10 : 0,
          background: 'transparent', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span aria-hidden style={{
          width: 26, height: 26, borderRadius: 8,
          display: 'grid', placeItems: 'center',
          background: 'rgba(30,95,255,0.18)',
          border: '1px solid rgba(30,95,255,0.45)',
          color: '#5BBEFC',
          flexShrink: 0,
        }}>
          <Zap size={13} strokeWidth={2.2} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5,
            color: '#5BBEFC', letterSpacing: '0.14em', textTransform: 'uppercase',
          }}>Elevate Influence School</div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
            color: 'var(--t1)', marginTop: 1,
          }}>Client Démo · {openTasks.length} en attente</div>
        </div>
        <span aria-hidden style={{
          color: 'var(--t3)', padding: 6,
          transition: 'transform 0.15s',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
        }}>
          <ChevronRight size={14} />
        </span>
      </button>

      {/* Cash reminder banner */}
      {overdueCash.length > 0 && (
        <div
          data-testid="elevate-cash-banner"
          style={{
            padding: '8px 10px', borderRadius: 8, marginBottom: 8,
            background: 'rgba(245,197,106,0.14)',
            border: '1px solid rgba(245,197,106,0.45)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <span style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: 9,
            padding: '2px 6px', borderRadius: 999,
            background: 'rgba(245,197,106,0.25)', color: '#F5C56A',
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>Cash</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
              color: 'var(--t1)',
            }}>
              {overdueCash.length} relance{overdueCash.length > 1 ? 's' : ''} en retard
            </div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
              color: 'var(--t3)', marginTop: 1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {overdueCash.slice(0, 2).map((r) => `${r.client_name} · ${r.amount}€`).join(' · ')}
            </div>
          </div>
        </div>
      )}

      {/* Tasks list — hidden when collapsed to reduce visual noise */}
      {expanded && (openTasks.length === 0 ? (
        <div style={{
          padding: '12px', textAlign: 'center',
          fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5,
          color: 'var(--t3)', fontStyle: 'italic',
        }}>Aucune tâche Elevate en attente</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {openTasks.map((t) => {
            const isOverdue = t.due_date && new Date(`${t.due_date}T${t.due_time || '12:00'}:00`) < new Date();
            const isDone = t.status === 'done';
            return (
              <div
                key={t.id}
                data-testid={`elevate-card-task-${t.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', borderRadius: 8,
                  background: 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isOverdue ? 'rgba(255,107,107,0.3)' : 'transparent'}`,
                  opacity: isDone ? 0.55 : 1,
                }}
              >
                <button
                  data-testid={`elevate-card-check-${t.id}`}
                  onClick={() => toggle(t)}
                  title={isDone ? 'Remettre à faire' : 'Marquer comme fait'}
                  style={{
                    width: 22, height: 22, borderRadius: 6,
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                    background: isDone ? 'rgba(52,211,153,0.20)' : 'transparent',
                    border: `1.5px solid ${isDone ? '#34D399' : 'var(--bd)'}`,
                    color: isDone ? '#34D399' : 'var(--t3)',
                    cursor: 'pointer', fontSize: 12, fontWeight: 800,
                  }}
                >{isDone ? '✓' : ''}</button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                    color: 'var(--t1)',
                    textDecoration: isDone ? 'line-through' : 'none',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{t.client_name ? `${t.client_name} · ${t.title}` : t.title}</div>
                  <div style={{
                    fontFamily: 'Sora', fontWeight: 600, fontSize: 10,
                    color: isOverdue && !isDone ? '#FF6B6B' : 'var(--t4)', marginTop: 1,
                  }}>
                    {t.due_date}{t.due_time ? ` · ${t.due_time}` : ''}
                    {isOverdue && !isDone && ' · En retard'}
                  </div>
                </div>
                {/* Iter 62bn — Delete task from workspace (founder only) */}
                <button
                  data-testid={`elevate-card-delete-${t.id}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Supprimer la tâche « ${t.title} » du workspace ?`)) return;
                    try {
                      const r = await deleteElevateTask(t.id);
                      toast.success(r.remote_synced ? 'Tâche supprimée ✓' : 'Tâche masquée localement');
                      if (navigator.vibrate) navigator.vibrate(15);
                      // Optimistic remove
                      setLocalTasks((prev) => (prev ?? fetched).filter((x) => x.id !== t.id));
                      window.dispatchEvent(new CustomEvent('asa-elevate-refresh'));
                    } catch (_) {
                      toast.error('Erreur suppression');
                    }
                  }}
                  title="Supprimer du workspace"
                  style={{
                    width: 22, height: 22, borderRadius: 6,
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                    background: 'rgba(240,97,107,0.10)',
                    border: '1px solid rgba(240,97,107,0.30)',
                    color: '#F0616B',
                    cursor: 'pointer',
                  }}
                ><X size={11} strokeWidth={2.3} /></button>
                <CheckSquare size={11} color="#5BBEFC" />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
